import {
  SupplierItem,
  BreadcrumbItem,
  BlogArticleItem,
  FAQItem,
} from '../types/schema-org';
import config from '../../config';
import { rewriteImgixToImageCdn } from '../constants/imageCdn';

// Generate breadcrumb schema
export function generateBreadcrumbSchema(
  breadcrumbs: BreadcrumbItem[]
): string {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => {
      const isLast = index === breadcrumbs.length - 1;
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name || 'Unnamed item',
        ...(isLast ? {} : { item: crumb.url }),
      };
    }),
  };
  return JSON.stringify(schema);
}

// Generate breadcrumbs from page data
export function generateBreadcrumbsFromPageData(
  pageData: any
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [
    { name: 'Home', url: `${config.baseUri}/` },
  ];

  // Ensure we have valid data before processing
  const vertical =
    pageData?.seoVertical || pageData?.vertical?.name || 'catering';
  const location = pageData?.location?.name || pageData?.locationText || null;
  const tag = pageData?.seoTag || pageData?.category?.name || null;

  // Add vertical breadcrumb
  if (vertical) {
    const verticalName = vertical.charAt(0).toUpperCase() + vertical.slice(1);
    const verticalSlug = vertical.toLowerCase();
    breadcrumbs.push({
      name: verticalName,
      url: `${config.baseUri}/listings/${verticalSlug}`,
    });
  }

  // Add location breadcrumb
  if (location && vertical) {
    const locationSlug = location.toLowerCase().replace(/\s+/g, '-');
    const verticalSlug = vertical.toLowerCase();
    breadcrumbs.push({
      name: location,
      url: `${config.baseUri}/listings/${verticalSlug}/${locationSlug}`,
    });
  }

  // Add tag/category breadcrumb
  if (tag && vertical) {
    const tagSlug = tag.toLowerCase().replace(/\s+/g, '-');
    const verticalSlug = vertical.toLowerCase();
    const locationSlug = location
      ? location.toLowerCase().replace(/\s+/g, '-')
      : '';
    const locationPath = locationSlug ? `/${locationSlug}` : '';
    breadcrumbs.push({
      name: tag,
      url: `${config.baseUri}/listings/${verticalSlug}${locationPath}/${tagSlug}`,
    });
  }

  return breadcrumbs;
}

// Helper function to convert supplier data to SupplierItem
export function mapSupplierToSchemaItem(
  supplier: any,
  locationText?: string
): SupplierItem {
  const rating = supplier.average_rating || supplier.external?.rating?.overall;
  const reviewCount = supplier.external?.numReviews || supplier.reviewCount;

  const generateUrl = () => {
    if (supplier.slug && supplier.slug.trim() !== '') {
      return `${config.baseUri}/suppliers/${supplier.slug}`;
    }
    const supplierName = supplier.name || 'unknown-supplier';
    const slugFromName = supplierName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${config.baseUri}/suppliers/${slugFromName}`;
  };

  const image = supplier.public?.images?.[0]?.url
    ? rewriteImgixToImageCdn(supplier.public.images[0].url)
    : undefined;

  return {
    name: supplier.name || 'Unknown Supplier',
    serviceType: supplier.categoryTier1 || 'catering',
    areaServed: locationText || 'UK',
    rating: rating ? parseFloat(rating) : undefined,
    reviewCount: reviewCount ? parseInt(reviewCount) : undefined,
    streetAddress:
      supplier.public?.address?.street ||
      supplier.address?.street ||
      `${locationText} Area`,
    addressCountry:
      supplier.public?.address?.countryCode ||
      supplier.address?.countryCode ||
      'GB',
    image,
    url: generateUrl(),
    slug: supplier.slug || undefined,
  };
}

// Generate Organization schema for the @graph
export function generateOrganizationSchemaForGraph() {
  return {
    '@type': 'Organization',
    '@id': `${config.baseUri}/#organization`,
    name: 'Togather',
    url: config.baseUri,
    logo:
      'https://fc-use1-00-pics-bkt-00.s3.amazonaws.com/a60551760aa5e6869c24eee493c6c92853fd764609dfeee269a486758c1558d2/f_marketingpicFull/u_3df6364e7691522eb47cb72db1f3c87e8b85804f8c1ddfc2ae9042f60d07b747/img_v7girvv719_fde919b55deb816db70c29ca7c9ca7f4dff4718e177f2cab231c0ac9bbd8928b.png',
    image:
      'https://fc-use1-00-pics-bkt-00.s3.amazonaws.com/a60551760aa5e6869c24eee493c6c92853fd764609dfeee269a486758c1558d2/f_marketingpicFull/u_3df6364e7691522eb47cb72db1f3c87e8b85804f8c1ddfc2ae9042f60d07b747/img_v7girvv719_fde919b55deb816db70c29ca7c9ca7f4dff4718e177f2cab231c0ac9bbd8928b.png',
    description:
      'Togather is a UK-based marketplace that helps you find the best suppliers for your event.',
    telephone: '+44 (0) 203 905 7439',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '68-80 Hanbury Street',
      addressLocality: 'London',
      addressRegion: 'Greater London',
      postalCode: 'E1 5JL',
      addressCountry: 'GB',
    },
  };
}

// Generate CollectionPage schema for the @graph
export function generateCollectionPageSchemaForGraph(
  pageTitle: string,
  pageUrl: string
) {
  return {
    '@type': 'CollectionPage',
    '@id': pageUrl,
    name: pageTitle,
    url: pageUrl,
    breadcrumb: { '@id': `${pageUrl}#breadcrumb` },
    mainEntity: { '@id': `${pageUrl}#itemlist` },
    description: `Discover and compare ${pageTitle.toLowerCase()} suppliers on Togather.`,
  };
}

// Generate BreadcrumbList schema for the @graph
export function generateBreadcrumbListSchemaForGraph(
  breadcrumbSchema: string,
  pageUrl: string
) {
  try {
    // Parse the breadcrumb schema from string
    const parsedBreadcrumb = JSON.parse(breadcrumbSchema);
    // Extract just the BreadcrumbList data without @context
    return {
      '@type': 'BreadcrumbList',
      '@id': `${pageUrl}#breadcrumb`,
      itemListElement: parsedBreadcrumb.itemListElement.map((item: any) => ({
        '@type': 'ListItem',
        position: item.position,
        item: {
          '@type': 'WebPage',
          ...(item.item && { '@id': item.item }),
          name: item.name,
        },
      })),
    };
  } catch (error) {
    return null; // Return null if parsing fails
  }
}

// Generate suppliers ItemList schema for the @graph
export function generateSuppliersItemListSchemaForGraph(
  suppliers: SupplierItem[],
  pageTitle: string,
  pageUrl: string
) {
  const getSupplierType = (serviceType: string): string[] => {
    // Normalize the service type
    const normalizedType = serviceType
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-') // spaces to hyphens
      .replace(/^the-/, '') // remove leading "the"
      .replace(/-+/g, '-'); // collapse multiple hyphens

    const typeMap: { [key: string]: string[] } = {
      // Venues
      venues: ['EventVenue', 'Place'],
      venue: ['EventVenue', 'Place'],
      'event-venue': ['EventVenue', 'Place'],
      'event-space': ['EventVenue', 'Place'],

      // Mobile Bars
      'mobile-bars': ['BarOrPub'],
      'mobile-bar': ['BarOrPub'],
      bar: ['BarOrPub'],
      drinks: ['BarOrPub'],
      drink: ['BarOrPub'],

      // Photographers
      photographers: ['ProfessionalService', 'Organization'],
      photographer: ['ProfessionalService', 'Organization'],
      photography: ['ProfessionalService', 'Organization'],
      photo: ['ProfessionalService', 'Organization'],

      // Catering
      catering: ['FoodTruck'],
      food: ['FoodTruck'],
      'food-truck': ['FoodTruck'],
      'street-food': ['FoodTruck'],
      'food-service': ['FoodTruck'],
    };

    const type = typeMap[normalizedType];
    return type || ['LocalBusiness'];
  };

  return {
    '@type': 'ItemList',
    '@id': `${pageUrl}#itemlist`,
    name: pageTitle,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    numberOfItems: suppliers.length,
    itemListElement: suppliers.map((supplier, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: supplier.url,
      item: {
        '@type': ['LocalBusiness', ...getSupplierType(supplier.serviceType)],
        '@id': supplier.url,
        name: supplier.name,
        url: supplier.url,
        description: `${supplier.serviceType} services`,
        ...(supplier.image && { image: supplier.image }),
        address: {
          '@type': 'PostalAddress',
          streetAddress:
            supplier.streetAddress || `${supplier.areaServed} Area`,
          addressLocality: supplier.areaServed,
          addressCountry: supplier.addressCountry || 'GB',
        },
        ...(supplier.rating && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: supplier.rating,
            reviewCount: supplier.reviewCount ?? 1,
          },
        }),
      },
    })),
  };
}

// Generate blog articles ItemList schema for the @graph
export function generateBlogArticlesItemListSchemaForGraph(
  blogArticles: BlogArticleItem[],
  pageUrl: string
) {
  return {
    '@type': 'ItemList',
    '@id': `${pageUrl}#related-content`,
    name: 'Related content',
    itemListElement: blogArticles.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'BlogPosting',
        headline: article.title,
        url: article.url,
      },
    })),
  };
}

// Generate FAQ schema for the @graph
export function generateFAQSchemaForGraph(faqs: FAQItem[], pageUrl: string) {
  return {
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}
